import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Trash2, Truck, Sparkles, Download } from "lucide-react";
import { downloadCsv } from "@/lib/download";

const STATUSES = ["Received", "Acknowledged", "In Production", "Dispatched"];

const COL = "border-r border-slate-200 last:border-r-0 px-2 py-2 text-[11px] text-slate-800 align-middle";
const HDR = "border-r border-emerald-800 last:border-r-0 px-2 py-2 text-[10px] uppercase tracking-wide text-white font-semibold whitespace-nowrap text-center";

export default function PODetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dispatchModal, setDispatchModal] = useState(null); // { idx, qty, invoice_no, invoice_date, notes }

  useEffect(() => {
    api.get(`/po/${id}`).then((r) => setPo(r.data)).catch(() => toast.error("PO not found"));
  }, [id]);

  if (!po) return <Layout title="Purchase Order"><div className="text-sm text-gray-500">Loading...</div></Layout>;

  const setField = (k, v) => setPo({ ...po, [k]: v });
  const setLI = (idx, patch) => {
    const items = [...po.line_items];
    items[idx] = { ...items[idx], ...patch };
    setPo({ ...po, line_items: items });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        doc_type: po.doc_type,
        customer_name: po.customer_name,
        po_number: po.po_number,
        po_date: po.po_date,
        due_date: po.due_date,
        currency: po.currency,
        status: po.status,
        notes: po.notes,
        line_items: po.line_items,
      };
      const res = await api.patch(`/po/${id}`, payload);
      setPo(res.data);
      toast.success("Saved");
    } catch (e) { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const saveDecoded = async (idx) => {
    const li = po.line_items[idx];
    if (!li.material_no && !li.part_code) { toast.error("Material No. required to save to catalog"); return; }
    try {
      const res = await api.post(`/po/${id}/line-item/${idx}/save-decoded`, {
        material_no: li.material_no || li.part_code,
        description: li.description,
        hose_spec: li.hose_spec,
        hose_length_mm: li.hose_length_mm,
        fitting_a: li.fitting_a,
        fitting_a_per_assembly: li.quantity > 0 ? (Number(li.fitting_a_qty || 0) / Number(li.quantity)) : 1,
        fitting_b: li.fitting_b,
        fitting_b_per_assembly: li.quantity > 0 ? (Number(li.fitting_b_qty || 0) / Number(li.quantity)) : 1,
        sleeve: li.sleeve,
        sleeve_per_assembly: li.quantity > 0 ? (Number(li.sleeve_qty || 0) / Number(li.quantity)) : 0,
      });
      setPo(res.data);
      toast.success("Saved to catalog — future POs will auto-match");
    } catch (e) { toast.error("Failed"); }
  };

  const dispatch = (idx) => {
    const li = po.line_items[idx];
    setDispatchModal({
      idx,
      qty: String(li.pending_qty || ""),
      invoice_no: "",
      invoice_date: new Date().toISOString().slice(0, 10),
      notes: "",
    });
  };

  const submitDispatch = async () => {
    if (!dispatchModal) return;
    const { idx, qty, invoice_no, invoice_date, notes } = dispatchModal;
    const q = parseFloat(qty);
    if (!q || q <= 0) { toast.error("Invalid quantity"); return; }
    if (!invoice_no.trim()) { toast.error("Invoice number required"); return; }
    try {
      const res = await api.post(`/po/${id}/line-item/${idx}/dispatch`, {
        dispatch_qty: q,
        invoice_no: invoice_no.trim(),
        invoice_date,
        notes,
      });
      setPo(res.data);
      setDispatchModal(null);
      toast.success(`Dispatched ${q} · Invoice ${invoice_no}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deletePO = async () => {
    if (!window.confirm("Delete this PO?")) return;
    await api.delete(`/po/${id}`);
    toast.success("PO deleted");
    navigate("/orders");
  };

  return (
    <Layout
      title={po.po_number ? `${po.doc_type || "PO"} ${po.po_number}` : "Purchase Order"}
      subtitle={po.customer_name || ""}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => downloadCsv(`/export/po/${id}`, `po_${po.po_number || id}.csv`)} data-testid="po-export-btn" className="text-xs text-slate-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={deletePO} data-testid="po-delete-btn" className="text-xs text-red-600 border border-red-200 rounded-md px-3 py-2 hover:bg-red-50 transition-colors inline-flex items-center gap-2">
            <Trash2 size={14} /> Delete
          </button>
          <button onClick={save} disabled={saving} data-testid="po-save-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      }
    >
      {/* Top header inputs */}
      <div className="bg-white border border-gray-200 rounded-md p-5 mb-6">
        <div className="text-[11px] uppercase tracking-widest font-mono text-gray-500 mb-3">PO Header</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            ["PO / DO", "doc_type", "select"],
            ["Party Name", "customer_name"],
            ["PO No", "po_number"],
            ["PO Date", "po_date"],
            ["Due Date", "due_date"],
            ["Currency", "currency"],
          ].map(([label, k, type]) => (
            <div key={k}>
              <label className="text-[10px] uppercase text-gray-500 font-mono tracking-widest">{label}</label>
              {type === "select" ? (
                <select value={po[k] || "PO"} onChange={(e) => setField(k, e.target.value)} data-testid={`field-${k}`} className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-white">
                  <option value="PO">PO</option>
                  <option value="DO">DO</option>
                </select>
              ) : (
                <input data-testid={`field-${k}`} value={po[k] || ""} onChange={(e) => setField(k, e.target.value)} className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1.5" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Decode table (Tally-style) */}
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden mb-6">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div>
            <div className="font-display text-base font-semibold text-slate-900">Decoded Line Items</div>
            <div className="text-xs text-gray-500 mt-0.5">Fields are editable. Save changes when done.</div>
          </div>
          <div className="text-xs text-gray-500 font-mono">
            Subtotal: {po.currency} {Number(po.subtotal || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} · Pending: {po.currency} {Number(po.pending_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1600px] w-full border-collapse text-left" data-testid="decoded-table">
            <thead>
              <tr className="bg-emerald-900">
                {["SL. NO.", "PARTY NAME", "PO/DO", "PO NO", "PO Date", "DUE Date", "Part Name", "Material No.", "Qty", "Pending Qty", "Status", "Rate", "Pending Total", "Hose", "QTY", "Fitting A", "QTY", "Fitting B", "QTY", "Sleeve", "QTY", "Remark 1", "Remark 2", "Actions"].map((h, i) => (
                  <th key={`${h}-${i}`} className={HDR}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {po.line_items.map((li, idx) => {
                const closed = li.line_status === "CLOSED";
                return (
                  <tr key={li.sr_no || idx} data-testid={`decoded-row-${idx}`} className={`border-t border-slate-200 ${closed ? "bg-emerald-50/40" : "bg-white"}`}>
                    <td className={COL + " font-mono font-semibold text-slate-900 text-center"}>{li.sr_no || "—"}</td>
                    <td className={COL + " whitespace-nowrap"}>{po.customer_name || "—"}</td>
                    <td className={COL + " text-center font-mono"}>{po.doc_type || "PO"}</td>
                    <td className={COL + " font-mono whitespace-nowrap"}>{po.po_number || "—"}</td>
                    <td className={COL + " font-mono whitespace-nowrap"}>{po.po_date || "—"}</td>
                    <td className={COL + " font-mono whitespace-nowrap"}>{po.due_date || "—"}</td>
                    <td className={COL + " whitespace-nowrap"}>
                      <input value={li.part_name || ""} onChange={(e) => setLI(idx, { part_name: e.target.value })} className="w-24 text-[11px] bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL + " font-mono whitespace-nowrap"}>
                      <input value={li.material_no || li.part_code || ""} onChange={(e) => setLI(idx, { material_no: e.target.value, part_code: e.target.value })} className="w-28 text-[11px] font-mono bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL + " text-right font-mono"}>
                      <input type="number" value={li.quantity || 0} onChange={(e) => setLI(idx, { quantity: parseFloat(e.target.value) || 0 })} className="w-16 text-[11px] text-right bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL + " text-right font-mono"}>
                      <input type="number" value={li.pending_qty ?? li.quantity} onChange={(e) => setLI(idx, { pending_qty: parseFloat(e.target.value) || 0 })} className="w-16 text-[11px] text-right bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL + " text-center"}>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${closed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {li.line_status || "OPEN"}
                      </span>
                    </td>
                    <td className={COL + " text-right font-mono"}>
                      <input type="number" value={li.unit_price || 0} onChange={(e) => setLI(idx, { unit_price: parseFloat(e.target.value) || 0 })} className="w-16 text-[11px] text-right bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL + " text-right font-mono font-semibold text-slate-900"}>{Number((li.pending_qty ?? li.quantity) * (li.unit_price || 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                    <td className={COL}>
                      <input value={li.hose_spec || ""} onChange={(e) => setLI(idx, { hose_spec: e.target.value })} placeholder="e.g. 1/2&quot;R1 X 1200MM" className="w-40 text-[11px] font-mono bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL + " text-right font-mono text-slate-700"}>{Number(li.hose_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                    <td className={COL}>
                      <input value={li.fitting_a || ""} onChange={(e) => setLI(idx, { fitting_a: e.target.value })} placeholder='1/2" BSP ST' className="w-32 text-[11px] bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL + " text-right font-mono"}>
                      <input type="number" value={li.fitting_a_qty || 0} onChange={(e) => setLI(idx, { fitting_a_qty: parseFloat(e.target.value) || 0 })} className="w-14 text-[11px] text-right bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL}>
                      <input value={li.fitting_b || ""} onChange={(e) => setLI(idx, { fitting_b: e.target.value })} placeholder='1/2" BSP ST' className="w-32 text-[11px] bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL + " text-right font-mono"}>
                      <input type="number" value={li.fitting_b_qty || 0} onChange={(e) => setLI(idx, { fitting_b_qty: parseFloat(e.target.value) || 0 })} className="w-14 text-[11px] text-right bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL}>
                      <input value={li.sleeve || ""} onChange={(e) => setLI(idx, { sleeve: e.target.value })} placeholder="—" className="w-24 text-[11px] bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL + " text-right font-mono"}>
                      <input type="number" value={li.sleeve_qty || 0} onChange={(e) => setLI(idx, { sleeve_qty: parseFloat(e.target.value) || 0 })} className="w-14 text-[11px] text-right bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL}>
                      <input value={li.remark_1 || ""} onChange={(e) => setLI(idx, { remark_1: e.target.value })} className="w-28 text-[11px] bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL}>
                      <input value={li.remark_2 || ""} onChange={(e) => setLI(idx, { remark_2: e.target.value })} className="w-28 text-[11px] bg-transparent focus:bg-white border-b border-transparent focus:border-slate-300 focus:outline-none" />
                    </td>
                    <td className={COL + " whitespace-nowrap"}>
                      <div className="flex items-center gap-1">
                        <button onClick={() => dispatch(idx)} disabled={closed} data-testid={`dispatch-${idx}`} title="Dispatch" className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                          <Truck size={11} /> Disp
                        </button>
                        <button onClick={() => saveDecoded(idx)} data-testid={`save-decoded-${idx}`} title="Save decode to catalog" className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50">
                          <Sparkles size={11} /> Save
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status timeline + notes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white border border-gray-200 rounded-md p-6">
          <div className="text-xs uppercase tracking-widest font-mono text-gray-500 mb-4">Status</div>
          <div className="space-y-2">
            {STATUSES.map((s, i) => {
              const active = po.status === s;
              const passed = STATUSES.indexOf(po.status) >= i;
              return (
                <button key={s} onClick={() => setField("status", s)} data-testid={`status-${s.replace(/\s+/g, "-").toLowerCase()}`} className="w-full text-left flex items-center gap-3">
                  <div className="relative">
                    <div className={`w-3 h-3 rounded-full border-2 ${passed ? "bg-slate-900 border-slate-900" : "bg-white border-gray-300"}`} />
                    {i < STATUSES.length - 1 && <div className={`absolute left-1/2 -translate-x-1/2 top-3 w-0.5 h-8 ${passed && STATUSES.indexOf(po.status) > i ? "bg-slate-900" : "bg-gray-200"}`} />}
                  </div>
                  <div className={`text-sm ${active ? "text-slate-900 font-semibold" : "text-gray-600"}`}>{s}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-md p-6">
          <div className="text-xs uppercase tracking-widest font-mono text-gray-500 mb-2">Notes / File</div>
          <textarea
            value={po.notes || ""}
            onChange={(e) => setField("notes", e.target.value)}
            rows={3}
            className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:outline-none"
            placeholder="Internal notes..."
          />
          <div className="mt-3 text-xs text-gray-500 font-mono">Uploaded: {po.file_name || "—"} · Created {po.created_at?.slice(0, 10)}</div>
        </div>
      </div>

      {dispatchModal && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4" onClick={() => setDispatchModal(null)} data-testid="dispatch-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display text-lg font-semibold text-slate-900">Record dispatch</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Line #{po.line_items[dispatchModal.idx]?.sr_no} · Material {po.line_items[dispatchModal.idx]?.material_no} · Pending {po.line_items[dispatchModal.idx]?.pending_qty}
                </div>
              </div>
              <button onClick={() => setDispatchModal(null)} className="text-gray-400 hover:text-slate-900"><Trash2 size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase text-gray-500 font-mono tracking-widest">Quantity dispatched</label>
                <input type="number" value={dispatchModal.qty} onChange={(e) => setDispatchModal({ ...dispatchModal, qty: e.target.value })} data-testid="dispatch-qty-input" className="mt-1 w-full text-sm font-mono border border-gray-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase text-gray-500 font-mono tracking-widest">Invoice No <span className="text-red-500">*</span></label>
                  <input value={dispatchModal.invoice_no} onChange={(e) => setDispatchModal({ ...dispatchModal, invoice_no: e.target.value })} placeholder="INV-2026-0142" data-testid="dispatch-invoice-input" className="mt-1 w-full text-sm font-mono border border-gray-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-gray-500 font-mono tracking-widest">Invoice Date</label>
                  <input type="date" value={dispatchModal.invoice_date} onChange={(e) => setDispatchModal({ ...dispatchModal, invoice_date: e.target.value })} data-testid="dispatch-date-input" className="mt-1 w-full text-sm font-mono border border-gray-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase text-gray-500 font-mono tracking-widest">Notes</label>
                <input value={dispatchModal.notes} onChange={(e) => setDispatchModal({ ...dispatchModal, notes: e.target.value })} placeholder="e.g. Partial shipment via BlueDart" className="mt-1 w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:outline-none" />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={() => setDispatchModal(null)} className="text-sm text-gray-600 border border-gray-200 rounded-md px-4 py-2 hover:bg-gray-50">Cancel</button>
              <button onClick={submitDispatch} data-testid="dispatch-submit-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-2">
                <Truck size={14} /> Record dispatch
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
